import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { AgentAuthProvider } from './context/AgentAuthContext';
import { StaffAuthProvider } from './context/StaffAuthContext';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import SearchResults from './pages/SearchResults';
import RoundTripResults from './pages/RoundTripResults';
import InternationalRoundTripResults from './pages/InternationalRoundTripResults';
import MultiCitySearchResults from './pages/MultiCitySearchResults';
import BookingPage from './pages/BookingPage';
import BookingsPage from './pages/BookingsPage';
import ProfilePage from './pages/ProfilePage';
import HotelResultsPage from './pages/HotelResultsPage';
import HotelDetailPage from './pages/HotelDetailPage';
import HotelBookingPage from './pages/HotelBookingPage';
import HotelBookingSuccessPage from './pages/HotelBookingSuccessPage';
import AgentSignup from './pages/AgentSignup';
import AdminLogin from './pages/admin/AdminLogin';
import AdminRoute from './components/guards/AdminRoute';
import AgentRoute from './components/guards/AgentRoute';
import UserRoute from './components/guards/UserRoute';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import RefundPolicy from './pages/legal/RefundPolicy';
import CancellationPolicy from './pages/legal/CancellationPolicy';
import ContactUs from './pages/legal/ContactUs';
import AboutUs from './pages/legal/AboutUs';
import WhyAcfTourism from './pages/legal/WhyAcfTourism';
import DealsPage from './pages/DealsPage';
import SeoPageRenderer from './pages/SeoPageRenderer';
import AdminDashboard from './pages/admin/AdminDashboard';
import DashboardOverview from './pages/admin/DashboardOverview';
import UserManagement from './pages/admin/UserManagement';
import AgentManagement from './pages/admin/AgentManagement';
import MarkupSettings from './pages/admin/MarkupSettings';
import WalletManagement from './pages/admin/WalletManagement';
import CreditManagement from './pages/admin/CreditManagement';
import BookingsManagement from './pages/admin/BookingsManagement';
import FlightBookingHub from './pages/admin/FlightBookingHub';
import HotelBookingsManagement from './pages/admin/HotelBookingsManagement';  // Phase H3
import OpsControlCenter from './pages/admin/OpsControlCenter';  // Feb-2026 — Live Booking Monitoring
import StaffManagement from './pages/admin/StaffManagement';  // Feb-2026 — M6 Staff Management
import RefundsTracker from './pages/admin/RefundsTracker';  // Feb-2026 — M7 PG Refund Tracker
import OpsInsights from './pages/admin/OpsInsights';  // Feb-2026 — M8 Ops Insights
import AdminSettings from './pages/admin/AdminSettings';
import SupplierSettings from './pages/admin/SupplierSettings';
import AirlineMaster from './pages/admin/AirlineMaster';
import SeoControlCenterLayout from './pages/admin/seo/SeoControlCenterLayout';
import SeoDashboard from './pages/admin/seo/SeoDashboard';
import SeoPagesList from './pages/admin/seo/SeoPagesList';
import SeoDataImport from './pages/admin/seo/SeoDataImport';
import SeoContentAudit from './pages/admin/seo/SeoContentAudit';
import SeoPriorityEntityScreen from './pages/admin/seo/SeoPriorityEntityScreen';
import SeoPageHealthScreen from './pages/admin/seo/SeoPageHealthScreen';
import SeoIndexingScreen from './pages/admin/seo/SeoIndexingScreen';
import SeoSitemapsScreen from './pages/admin/seo/SeoSitemapsScreen';
import SeoFaresScreen from './pages/admin/seo/SeoFaresScreen';
import SeoVisaDataScreen from './pages/admin/seo/SeoVisaDataScreen';
import SeoBaggageScreen from './pages/admin/seo/SeoBaggageScreen';
import SeoLogsScreen from './pages/admin/seo/SeoLogsScreen';
import SeoCronRunsScreen from './pages/admin/seo/SeoCronRunsScreen';
import SeoApiFailuresScreen from './pages/admin/seo/SeoApiFailuresScreen';
import AnalyticsDashboard from './pages/admin/AnalyticsDashboard';
import MarketingDashboard from './pages/admin/MarketingDashboard';
import NotificationsManagement from './pages/admin/NotificationsManagement';
import AdminAlertsInbox from './pages/admin/AdminAlertsInbox';
import ReportsDashboard from './pages/admin/ReportsDashboard';
import SupplierTripjackReport from './pages/admin/SupplierTripjackReport';
import GstSettings from './pages/admin/GstSettings';
import CommissionTdsReports from './pages/admin/CommissionTdsReports';
import ComplianceHubLayout from './pages/admin/ComplianceHubLayout';
import ComplianceOverview from './pages/admin/ComplianceOverview';
import ComplianceCaExports from './pages/admin/ComplianceCaExports';
import ComplianceCreditNotes from './pages/admin/ComplianceCreditNotes';
import GoLivePreflight from './pages/admin/GoLivePreflight';
import WhatsAppDashboard from './pages/admin/WhatsAppDashboard';
import WhatsAppLogs from './pages/admin/WhatsAppLogs';
import WhatsAppAgents from './pages/admin/WhatsAppAgents';
import WhatsAppBlocklist from './pages/admin/WhatsAppBlocklist';
import WhatsAppSimulator from './pages/admin/WhatsAppSimulator';
import WhatsAppSessionTranscript from './pages/admin/WhatsAppSessionTranscript';
import { SupportQueue, SupportTicketDetail } from './pages/admin/SupportDashboard';
import { MyTickets, MyTicketDetail } from './pages/MyTickets';
import AnalyticsTracker from './components/AnalyticsTracker';
import PushOptInMount from './components/PushOptInMount';
import UserDashboard from './pages/user/UserDashboard';
import DashboardHome from './pages/user/DashboardHome';
import MyProfile from './pages/user/MyProfile';
import MyPassengers from './pages/user/MyPassengers';
import MyBookings from './pages/user/MyBookings';
import InstallPrompt from './components/InstallPrompt';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import ReviewBooking from './pages/ReviewBooking';
import AncillaryServices from './pages/AncillaryServices';
import BookingSuccess from './pages/BookingSuccess';
import BookingDetails from './pages/BookingDetails';

// Agent Dashboard Pages
import AgentDashboardPage from './pages/agent/AgentDashboard';
import AgentLoginPage from './pages/agent/AgentLogin';
import AgentWallet from './pages/agent/AgentWallet';
import AgentCredit from './pages/agent/AgentCredit';
import AgentBookings from './pages/agent/AgentBookings';
import AgentHotelBookings from './pages/agent/AgentHotelBookings';  // Phase H4
import AgentTravellers from './pages/agent/AgentTravellers';
import AgentProfile from './pages/agent/AgentProfile';
import AgentReports from './pages/agent/AgentReports';
import AgentCommission from './pages/agent/AgentCommission';
import AgentSeatBlocks from './pages/agent/AgentSeatBlocks';
import AgentSupportTickets from './pages/agent/AgentSupportTickets';
import AgentSupportTicketDetail from './pages/agent/AgentSupportTicketDetail';
import AgentLayout from './pages/agent/AgentLayout';

// Staff Portal Pages
import StaffLogin from './pages/staff/StaffLogin';
import StaffLayout from './pages/staff/StaffLayout';
import StaffDashboard from './pages/staff/StaffDashboard';
import StaffSearchResults from './pages/staff/StaffSearchResults';
import StaffReviewBooking from './pages/staff/StaffReviewBooking';
import StaffBookings from './pages/staff/StaffBookings';
import StaffProfile from './pages/staff/StaffProfile';
import StaffRoute from './components/guards/StaffRoute';
import BranchManagement from './pages/admin/BranchManagement';
import StaffUserManagement from './pages/admin/StaffUserManagement';

// Offline Inventory Admin Pages
import OfflineInventoryDashboard from './pages/admin/OfflineInventoryDashboard';
import CreateOfflineFlight from './pages/admin/CreateOfflineFlight';
import InventoryManagement from './pages/admin/InventoryManagement';
import PassengerManifest from './pages/admin/PassengerManifest';
import OccasionLabelsAdmin from './pages/admin/OccasionLabelsAdmin';
import { SiteStatusProvider, useSiteStatus } from './context/SiteStatusContext';
import ComingSoonGate from './components/ComingSoonGate';
import HotelSearchGate from './components/HotelSearchGate';
import RobotsMetaManager from './components/RobotsMetaManager';
import NoIndexMeta from './components/NoIndexMeta';

function NoIndexRoute({ children }) {
  return (
    <>
      <NoIndexMeta />
      {children}
    </>
  );
}

const LEGAL_PATHS = new Set([
  '/terms',
  '/privacy',
  '/refund-policy',
  '/cancellation-policy',
  '/contact',
  '/contact-us',
  '/about-us',
  '/why-acf-tourism',
  '/deals',
]);

// Layout wrapper to conditionally show navbar
function Layout({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAgentRoute = location.pathname.startsWith('/agent');
  const isStaffRoute = location.pathname.startsWith('/staff');
  const isLegalRoute = LEGAL_PATHS.has(location.pathname);
  const { comingSoonEnabled, loading } = useSiteStatus();
  const hideNavbar =
    !loading &&
    comingSoonEnabled &&
    !isAdminRoute &&
    !isAgentRoute &&
    !isStaffRoute &&
    !isLegalRoute;

  return (
    <>
      <RobotsMetaManager />
      {!isAdminRoute && !isStaffRoute && !hideNavbar && <Navbar />}
      {children}
    </>
  );
}

function App() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  useEffect(() => {
    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
    }

    // Check if app can be installed
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show install banner after 3 seconds
      setTimeout(() => {
        setShowInstallPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }
    
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismissInstall = () => {
    setShowInstallPrompt(false);
  };

  return (
    <div className="App">
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <SiteStatusProvider>
        <AuthProvider>
          <AgentAuthProvider>
          <StaffAuthProvider>
            <AnalyticsTracker />
            <PushOptInMount />
            <Layout>
              {showInstallPrompt && (
              <InstallPrompt 
                onInstall={handleInstallClick} 
                onDismiss={handleDismissInstall} 
              />
            )}
            <Routes>
            {/* Legal / compliance pages (CCAvenue-required) — always accessible */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/cancellation-policy" element={<CancellationPolicy />} />
            <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/why-acf-tourism" element={<WhyAcfTourism />} />
            <Route path="/deals" element={<DealsPage />} />

            <Route element={<ComingSoonGate />}>
            <Route path="/" element={<HomePage />} />

            {/* Public SEO entity pages — rendered SSR by the backend.
                In preview, /api/seo/render bridges the request through the
                /api/* proxy; in prod nginx serves these paths directly. */}
            <Route path="/flights" element={<SeoPageRenderer />} />
            <Route path="/flights/:slug" element={<SeoPageRenderer />} />
            <Route path="/airports" element={<SeoPageRenderer />} />
            <Route path="/airports/:slug" element={<SeoPageRenderer />} />
            <Route path="/airlines" element={<SeoPageRenderer />} />
            <Route path="/airlines/:slug" element={<SeoPageRenderer />} />
            <Route path="/countries" element={<SeoPageRenderer />} />
            <Route path="/countries/:slug" element={<SeoPageRenderer />} />
            <Route path="/destinations" element={<SeoPageRenderer />} />
            <Route path="/destinations/:slug" element={<SeoPageRenderer />} />
            <Route path="/visa" element={<SeoPageRenderer />} />
            <Route path="/visa/:slug" element={<SeoPageRenderer />} />
            <Route path="/search" element={<NoIndexRoute><SearchResults /></NoIndexRoute>} />
            <Route path="/round-trip" element={<NoIndexRoute><RoundTripResults /></NoIndexRoute>} />
            <Route path="/international-round-trip" element={<NoIndexRoute><InternationalRoundTripResults /></NoIndexRoute>} />
            <Route path="/multi-city" element={<NoIndexRoute><MultiCitySearchResults /></NoIndexRoute>} />
            <Route element={<HotelSearchGate />}>
              <Route path="/hotels/results" element={<NoIndexRoute><HotelResultsPage /></NoIndexRoute>} />
              <Route path="/hotels/book" element={<NoIndexRoute><HotelBookingPage /></NoIndexRoute>} />
              <Route path="/hotels/booking-success" element={<NoIndexRoute><HotelBookingSuccessPage /></NoIndexRoute>} />
              <Route path="/hotels/:hotelId" element={<NoIndexRoute><HotelDetailPage /></NoIndexRoute>} />
            </Route>
            <Route path="/booking" element={<NoIndexRoute><BookingPage /></NoIndexRoute>} />
            <Route path="/review-booking" element={<NoIndexRoute><ReviewBooking /></NoIndexRoute>} />
            <Route path="/ancillary-services" element={<NoIndexRoute><AncillaryServices /></NoIndexRoute>} />
            <Route path="/booking-success" element={<NoIndexRoute><BookingSuccess /></NoIndexRoute>} />
            <Route path="/booking-details/:bookingId" element={<NoIndexRoute><BookingDetails /></NoIndexRoute>} />
          <Route path="/bookings" element={<NoIndexRoute><BookingsPage /></NoIndexRoute>} />
          <Route path="/my-tickets" element={<NoIndexRoute><MyTickets /></NoIndexRoute>} />
          <Route path="/my-tickets/:id" element={<NoIndexRoute><MyTicketDetail /></NoIndexRoute>} />
          <Route path="/profile" element={<NoIndexRoute><ProfilePage /></NoIndexRoute>} />
          <Route path="/payment/success" element={<NoIndexRoute><PaymentSuccess /></NoIndexRoute>} />
          <Route path="/payment/cancel" element={<NoIndexRoute><PaymentCancel /></NoIndexRoute>} />
          <Route path="/user/dashboard" element={<UserRoute />}>
            <Route element={<UserDashboard />}>
              <Route index element={<DashboardHome />} />
              <Route path="profile" element={<MyProfile />} />
              <Route path="passengers" element={<MyPassengers />} />
              <Route path="bookings" element={<MyBookings />} />
              <Route path="tickets" element={<MyTickets />} />
              <Route path="tickets/:ticketId" element={<MyTickets />} />
            </Route>
          </Route>
          </Route>

          <Route path="/agent/login" element={<AgentLoginPage />} />
          <Route path="/agent/signup" element={<AgentSignup />} />
          <Route path="/agent/forgot-password" element={<ForgotPassword userType="agent" />} />
          <Route path="/agent/reset-password" element={<ResetPassword userType="agent" />} />
          
          {/* Agent Dashboard Routes */}
          <Route element={<AgentRoute />}>
            <Route path="/agent/dashboard" element={<AgentDashboardPage />} />
            <Route path="/agent/wallet" element={<AgentWallet />} />
            <Route path="/agent/credit" element={<AgentCredit />} />
            <Route path="/agent/bookings" element={<AgentBookings />} />
            <Route path="/agent/seat-blocks" element={<AgentSeatBlocks />} />
            <Route path="/agent/hotel-bookings" element={<AgentHotelBookings />} />
            <Route path="/agent/travellers" element={<AgentTravellers />} />
            <Route path="/agent/profile" element={<AgentProfile />} />
            <Route path="/agent/reports" element={<AgentReports />} />
            <Route path="/agent/commission" element={<AgentCommission />} />
            <Route path="/agent/support-tickets" element={<AgentLayout><AgentSupportTickets /></AgentLayout>} />
            <Route path="/agent/support-tickets/:id" element={<AgentLayout><AgentSupportTicketDetail /></AgentLayout>} />
          </Route>

          {/* Staff Counter Portal */}
          <Route path="/staff/login" element={<StaffLogin />} />
          <Route element={<StaffRoute />}>
            <Route element={<StaffLayout />}>
              <Route path="/staff/dashboard" element={<StaffDashboard />} />
              <Route path="/staff/search" element={<StaffSearchResults />} />
              <Route path="/staff/review" element={<StaffReviewBooking />} />
              <Route path="/staff/bookings" element={<StaffBookings />} />
              <Route path="/staff/profile" element={<StaffProfile />} />
            </Route>
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<ForgotPassword userType="admin" />} />
          <Route path="/admin/reset-password" element={<ResetPassword userType="admin" />} />
          <Route path="/admin" element={<AdminRoute />}>
            <Route element={<AdminDashboard />}>
            <Route path="dashboard" element={<DashboardOverview />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="agents" element={<AgentManagement />} />
            <Route path="bookings" element={<FlightBookingHub />} />
            <Route path="flights/bookings" element={<FlightBookingHub />} />
            <Route path="bookings-legacy" element={<BookingsManagement />} />
            <Route path="hotel-bookings" element={<HotelBookingsManagement />} />
            <Route path="ops-monitor" element={<OpsControlCenter />} />
            <Route path="staff" element={<StaffManagement />} />
            <Route path="branches" element={<BranchManagement />} />
            <Route path="staff-users" element={<StaffUserManagement />} />
            <Route path="refunds" element={<RefundsTracker />} />
            <Route path="ops-insights" element={<OpsInsights />} />
            <Route path="markup" element={<MarkupSettings />} />
            <Route path="wallet" element={<WalletManagement />} />
            <Route path="credit" element={<CreditManagement />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="supplier-settings" element={<SupplierSettings />} />
            <Route path="airlines" element={<AirlineMaster />} />
            <Route path="seo" element={<SeoControlCenterLayout />}>
              <Route index element={<SeoDashboard />} />
              <Route path="pages" element={<SeoPagesList />} />
              <Route path="priority/:entityType" element={<SeoPriorityEntityScreen />} />
              <Route path="entities/:entityType" element={<SeoPriorityEntityScreen />} />
              <Route path="import" element={<SeoDataImport />} />
              <Route path="audit" element={<SeoContentAudit />} />
              <Route path="health" element={<SeoPageHealthScreen />} />
              <Route path="indexing" element={<SeoIndexingScreen />} />
              <Route path="sitemaps" element={<SeoSitemapsScreen />} />
              <Route path="fares" element={<SeoFaresScreen />} />
              <Route path="visa-data" element={<SeoVisaDataScreen />} />
              <Route path="baggage" element={<SeoBaggageScreen />} />
              <Route path="cron-runs" element={<SeoCronRunsScreen />} />
              <Route path="api-failures" element={<SeoApiFailuresScreen />} />
              <Route path="logs" element={<SeoLogsScreen />} />
            </Route>
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="marketing" element={<MarketingDashboard />} />
            <Route path="notifications" element={<NotificationsManagement />} />
            <Route path="whatsapp" element={<WhatsAppDashboard />} />
            <Route path="whatsapp/logs" element={<WhatsAppLogs />} />
            <Route path="whatsapp/simulator" element={<WhatsAppSimulator />} />
            <Route path="whatsapp/agents" element={<WhatsAppAgents />} />
            <Route path="whatsapp/blocklist" element={<WhatsAppBlocklist />} />
            <Route path="whatsapp/session/:sessionId" element={<WhatsAppSessionTranscript />} />
            <Route path="alerts" element={<AdminAlertsInbox />} />
            <Route path="reports" element={<ReportsDashboard />} />
            <Route path="supplier-tripjack" element={<SupplierTripjackReport />} />
            <Route path="gst-settings" element={<Navigate to="/admin/compliance/settings" replace />} />
            <Route path="commission-tds" element={<Navigate to="/admin/compliance/commission-tds" replace />} />
            <Route path="compliance" element={<ComplianceHubLayout />}>
              <Route index element={<ComplianceOverview />} />
              <Route path="settings" element={<GstSettings />} />
              <Route path="commission-tds" element={<CommissionTdsReports />} />
              <Route path="gst-report" element={<ReportsDashboard initialReport="gst_report" />} />
              <Route path="ca-exports" element={<ComplianceCaExports />} />
              <Route path="credit-notes" element={<ComplianceCreditNotes />} />
            </Route>
            <Route path="go-live-preflight" element={<GoLivePreflight />} />
            <Route path="support" element={<SupportQueue />} />
            <Route path="support/:id" element={<SupportTicketDetail />} />
            <Route path="offline-inventory" element={<OfflineInventoryDashboard />} />
            <Route path="offline-inventory/create" element={<CreateOfflineFlight />} />
            <Route path="offline-inventory/:flightId/edit" element={<CreateOfflineFlight />} />
            <Route path="offline-inventory/:flightId/inventory" element={<InventoryManagement />} />
            <Route path="offline-inventory/:flightId/manifest/:date" element={<PassengerManifest />} />
            <Route path="occasion-labels" element={<OccasionLabelsAdmin />} />
            </Route>
          </Route>
        </Routes>
        </Layout>
          </StaffAuthProvider>
        </AgentAuthProvider>
        </AuthProvider>
        </SiteStatusProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
